/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';

/**
 * Internal Imports
 */
import Table from '../../../../shared/table';

import {
  isPercentLessThanOne,
  isPercentRoundedToOneHundred
} from '../../../../../utils/complianceFunctions';

/**
 * Global Type Definition Imports
 */
import { EmpComplianceReport } from '../../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  CellDiv,
  StyledBoldLabel,
  StyledLabel,
  StyledNeuTableRow
} from '../../../../shared/table/Table.styles';

interface IEmployeeComplianceReportTableProps {
  complianceList: EmpComplianceReport[];
  complianceTotal?: EmpComplianceReport | null;
  loading: boolean;
}
const EmployeeComplianceReportTable: FC<
  IEmployeeComplianceReportTableProps
> = ({ complianceList, complianceTotal, loading }) => {
  const [sortKey, setSortKey] = useState<string>('department');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Department');

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Department':
        key = 'department';
        break;
      case 'Department Id':
        key = 'departmentId';
        break;
      case 'Total':
        key = 'total';
        break;
      case 'Rounded':
        key = 'roundedNumber';
        break;
      case 'Not Rounded':
        key = 'notRoundedNumber';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const sortedCompliance = useMemo(() => {
    const stateCopy = [...complianceList];
    const sorted = stateCopy.sort(
      (a: EmpComplianceReport, b: EmpComplianceReport) => {
        if (!(sortKey === 'department' || sortKey === 'departmentId')) {
          if (a[sortKey] === b[sortKey]) {
            return 0;
          }
          if (sortDir === 'asc') {
            return (a[sortKey] as number) - (b[sortKey] as number);
          }
          return (b[sortKey] as number) - (a[sortKey] as number);
        }
        if (a[sortKey] === b[sortKey]) {
          return 0;
        }
        if (!a[sortKey]) {
          return sortDir === 'asc' ? 1 : -1;
        }
        if (!b[sortKey]) {
          return sortDir === 'asc' ? -1 : 1;
        }
        if (sortDir === 'asc') {
          return a[sortKey]! > b[sortKey]! ? 1 : -1;
        }
        return a[sortKey]! > b[sortKey]! ? -1 : 1;
      }
    );
    return sorted;
  }, [complianceList, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () => sortedCompliance.length === 0,
    [sortedCompliance]
  );

  const headers: JSX.Element[] = [
    <CellContainer
      key="Department"
      data-testid="Employee-Compliance-Department-Column"
      unit="column"
      colWidth={30}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      Department
    </CellContainer>,
    <CellContainer
      key="Department Id"
      id="Employee-Compliance-Department-Id-Column"
      unit="column"
      colWidth={18}
      direction={!(active === 'Department Id') ? 'none' : sortDir}
      sort
    >
      Department Id
    </CellContainer>,
    <CellContainer
      key="Total"
      id="Employee-Compliance-Total-Column"
      unit="column"
      colWidth={17}
      direction={!(active === 'Total') ? 'none' : sortDir}
      sort
    >
      Total
    </CellContainer>,
    <CellContainer
      key="Rounded #"
      id="Employee-Compliance-Rounded-#-Column"
      unit="column"
      colWidth={17}
      direction={!(active === 'Rounded') ? 'none' : sortDir}
      sort
    >
      Rounded
    </CellContainer>,
    <CellContainer
      key="Not Rounded #"
      id="Employee-Compliance-Not-Rounded-#-Column"
      unit="column"
      colWidth={17}
      direction={!(active === 'Not Rounded') ? 'none' : sortDir}
      sort
    >
      Not Rounded
    </CellContainer>
  ];

  const rows = (
    complianceTotal
      ? [{ ...complianceTotal, totalRow: true }, ...sortedCompliance]
      : sortedCompliance
  ).map((item: EmpComplianceReport, i: number) => {
    const isRoundedPercentLessThanOne = isPercentLessThanOne(
      item.roundedNumber,
      item.total
    );
    const isNotRoundedPercentLessThanOne = isPercentLessThanOne(
      item.notRoundedNumber,
      item.total
    );
    const isRoundedPercentRoundedToOneHundred = isPercentRoundedToOneHundred(
      item.roundedNumber,
      item.total
    );
    const isNotRoundedPercentRoundedToOneHundred = isPercentRoundedToOneHundred(
      item.notRoundedNumber,
      item.total
    );

    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Employee-Compliance-Report-Row-${i}`}
        id={`Employee-Compliance-Report-Row-${item.unitId}`}
        data-testid={`Employee-Compliance-Report-Row-${item.unitId}`}
        unit="row"
        size="default"
        height="5em"
        styles={item.totalRow ? 'color: #093865; font-weight: 700;' : ''}
      >
        <CellContainer unit="column" colWidth={30}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            <div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  fontWeight: 700
                }}
              >
                {item.department}
                {/* {item.locationTypeDisplay &&
                  item.department.toLowerCase() !==
                    item.locationTypeDisplay.toString().toLowerCase() &&
                  ` | ${item.locationTypeDisplay}`} */}
              </div>
              {/* <div>{!item.unit.includes('Total') ? item.unitId : ''}</div> */}
            </div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={18}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            <div>
              {item.departmentId === 'Facility Total' ? '' : item.departmentId}
            </div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={17}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            <div>{item.total}</div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={17}>
          <CellDiv>
            <StyledBoldLabel>{item.roundedNumber}</StyledBoldLabel>
            <StyledLabel
              className={!isNotRoundedPercentLessThanOne ? 'ps-1' : ''}
            >
              {isRoundedPercentLessThanOne
                ? '<'
                : isRoundedPercentRoundedToOneHundred
                ? '>'
                : ''}
              {item.roundedPercent}%
            </StyledLabel>
          </CellDiv>
        </CellContainer>
        <CellContainer unit="column" colWidth={17}>
          <CellDiv>
            <StyledBoldLabel>{item.notRoundedNumber}</StyledBoldLabel>
            <StyledLabel
              className={!isNotRoundedPercentLessThanOne ? 'ps-1' : ''}
            >
              {isNotRoundedPercentLessThanOne
                ? '<'
                : isNotRoundedPercentRoundedToOneHundred
                ? '>'
                : ''}
              {item.notRoundedPercent}%
            </StyledLabel>
          </CellDiv>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <Table
      customClass="Employee-compliance-report-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      loading={loading}
      offset="70px"
      type="employeeComplianceReport"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

export default EmployeeComplianceReportTable;
